import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useDataCache } from '../contexts/DataCacheContext';
import configService from '../services/configService';
import { useToast } from './Toast';

function BusinessDescription({ onComplete, cachedBusinessConfig, adminBusinessId }) {
  const { user, updateUserProfile } = useAuth();
  const { updateCache } = useDataCache();
  const { showToast } = useToast();
  const [formData, setFormData] = useState({
    businessDescription: '',
    assistantName: ''
  });
  const [loading, setLoading] = useState(!cachedBusinessConfig);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const effectiveUserId = adminBusinessId || user?.attributes?.sub;

  // Auto-resize textarea when entering edit mode
  useEffect(() => {
    if (isEditing) {
      const textarea = document.querySelector('textarea');
      if (textarea) {
        setTimeout(() => {
          textarea.style.height = 'auto';
          textarea.style.height = textarea.scrollHeight + 'px';
        }, 0);
      }
    }
  }, [isEditing]);

  useEffect(() => {
    loadExistingOrGenerate();
    
    // Debug function to force show form
    window.debugForceBusinessDescForm = () => {
      setGenerating(false);
      setLoading(false);
      setError('');
      setFormData({
        businessDescription: 'Test business description',
        assistantName: 'Test Assistant'
      });
    };
    
    // Debug function to test new config flag
    window.debugTestNewConfigFlag = () => {
      sessionStorage.setItem('isNewConfig', 'true');
      loadExistingOrGenerate();
    };
  }, []);

  // Expose save function to Setup.js
  useEffect(() => {
    window.saveBusinessDescription = async () => {
      try {
        await updateBusinessConfig(formData);
        return true;
      } catch (error) {
        console.error('❌ BusinessDescription: Save failed:', error);
        setError('Failed to save business description. Please try again.');
        return false;
      }
    };
    
    return () => {
      delete window.saveBusinessDescription;
    };
  }, [formData]);
  
  const loadExistingOrGenerate = async () => {
    try {
      const config = cachedBusinessConfig || (adminBusinessId 
        ? await configService.getAdminUserConfig(effectiveUserId)
        : await configService.getBusinessConfigByUserId(effectiveUserId));

      console.log('[BusinessDescription] Loaded config:', {
        business_name: config?.business_name,
        has_description: !!config?.business_description,
        isAdminMode: !!adminBusinessId
      });

      if (config && config.business_description) {
        // Use existing data (assistant_name might be null, that's ok)
        setFormData({
          businessDescription: config.business_description,
          assistantName: config.assistant_name || 'Alex'
        });
        setLoading(false);
        return;
      }
      // If no existing data, generate new description
      generateBusinessDescription();
    } catch (error) {
      console.warn('📝 DEBUG: Could not load existing config, generating new:', error);
      generateBusinessDescription();
    }
  };

  const generateBusinessDescription = async () => {
    setGenerating(true);
    setError('');
    
    try {
      // Load business config from backend
      const cognitoUserId = effectiveUserId;
      if (!cognitoUserId) {
        throw new Error('User not authenticated');
      }
      
      const config = adminBusinessId
        ? await configService.getAdminUserConfig(cognitoUserId)
        : await configService.getBusinessConfigByUserId(cognitoUserId);
      if (!config || !config.business_name || !config.business_name.trim()) {
        throw new Error('Missing required business information');
      }
      
      // Generate description using the config server
      const response = await generateDescription();
      
      setFormData(prev => ({
        ...prev,
        businessDescription: response.generated_description || 'A professional business providing quality services.',
        assistantName: prev.assistantName || 'Alex'
      }));
      
      setIsEditing(true);
      setLoading(false);
      
    } catch (error) {
      console.error('Error generating description:', error);
      setError('Failed to generate business description. Please enter manually.');
      setFormData(prev => ({
        ...prev,
        businessDescription: 'A professional business providing quality services.',
        assistantName: 'Business Assistant'
      }));
      setLoading(false);
    } finally {
      setGenerating(false);
    }
  };



  const generateDescription = async () => {
    return await configService.generateBusinessDescription();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      // Update the config with the final description and assistant settings
      await updateBusinessConfig(formData);
      
      onComplete();
    } catch (error) {
      console.error('Error saving business description:', error);
      setError('Failed to save business description. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.assistantName.trim() && !formData.businessDescription.trim()) {
      return;
    }
    setSaving(true);
    try {
      await updateBusinessConfig(formData);
      showToast('Saved successfully!');
      if (onComplete) onComplete();
    } catch (error) {
      showToast('Failed to save', 'error');
      setError('Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const handleAssistantNameBlur = async () => {
    if (!formData.assistantName.trim()) {
      return;
    }
    setSaving(true);
    try {
      await updateBusinessConfig(formData);
      showToast('Saved successfully!');
      if (onComplete) onComplete();
    } catch (error) {
      showToast('Failed to save', 'error');
    } finally {
      setSaving(false);
    }
  };

  const updateBusinessConfig = async (data) => {
    const updateData = {
      business_description: data.businessDescription
    };
    if (data.assistantName) {
      updateData.assistant_name = data.assistantName;
    }
    
    const result = adminBusinessId
      ? await configService.updateAdminUserConfig(effectiveUserId, updateData)
      : await configService.updateBusinessConfig(effectiveUserId, updateData);
    
    const config = adminBusinessId
      ? await configService.getAdminUserConfig(effectiveUserId)
      : await configService.getBusinessConfigByUserId(effectiveUserId);
    updateCache('settings', { businessConfig: config });
    return result;
  };

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (generating || loading) {
    return (
      <div className="text-center py-8">
        <div className="w-10 h-10 border-4 border-gray-200 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-gray-600 text-sm">Loading assistant...</p>
      </div>
    );
  }

  return (
    <div>
      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded mb-4 text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="mb-6">
          <label className="block mb-2 font-medium text-gray-800 text-sm">
            Assistant Name
          </label>
          <div 
            className="relative border border-gray-300 rounded p-3 bg-white cursor-text min-h-[44px] flex items-center"
            onClick={() => {
              const input = document.getElementById('assistant-name-input');
              if (input) input.focus();
            }}
          >
            <input
              id="assistant-name-input"
              type="text"
              value={formData.assistantName}
              onChange={(e) => {
                setFormData({...formData, assistantName: e.target.value});
              }}
              onBlur={(e) => {
                handleAssistantNameBlur();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.target.blur();
                }
              }}
              placeholder="e.g., Sarah, Business Assistant"
              className="border-none outline-none bg-transparent text-sm w-full"
            />
          </div>
        </div>

        <div className="mb-6">
          <label className="block mb-2 font-medium text-gray-800 text-sm">
            AI Knowledge and Behavior
          </label>
          
          <div 
            className="relative border border-gray-300 rounded min-h-[120px] cursor-text"
            onClick={() => {
              setIsEditing(true);
              setTimeout(() => {
                const textarea = document.getElementById('business-desc-textarea');
                if (textarea) {
                  textarea.focus();
                  textarea.style.height = 'auto';
                  textarea.style.height = textarea.scrollHeight + 'px';
                }
              }, 0);
            }}
          >
            {isEditing ? (
              <textarea
                id="business-desc-textarea"
                value={formData.businessDescription}
                onChange={(e) => {
                  setFormData({...formData, businessDescription: e.target.value});
                  e.target.style.height = 'auto';
                  e.target.style.height = e.target.scrollHeight + 'px';
                }}
                onBlur={async () => {
                  if (formData.businessDescription.trim()) {
                    setSaving(true);
                    try {
                      await updateBusinessConfig(formData);
                      showToast('Saved successfully!');
                      if (onComplete) onComplete();
                    } catch (error) {
                      showToast('Failed to save', 'error');
                    } finally {
                      setSaving(false);
                    }
                  }
                  setIsEditing(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    setIsEditing(false);
                    e.target.blur();
                  }
                }}
                placeholder="Describe your business..."
                className="w-full min-h-[120px] p-3 border-none outline-none resize-none text-sm bg-transparent leading-relaxed"
              />
            ) : (
              <div className="p-3 text-sm leading-relaxed whitespace-pre-wrap min-h-[120px] relative">
                <span className={formData.businessDescription ? 'text-gray-800' : 'text-gray-600'}>
                  {formData.businessDescription || 'Click to add business description...'}
                </span>
                <div className="absolute top-2 right-2 opacity-50 text-xs text-gray-600">
                  <i className="fa fa-edit"></i>
                </div>
              </div>
            )}
          </div>
          
          <p className="text-[11px] text-gray-600 mt-1 leading-snug">
            This knowledge helps your AI assistant understand your business and behave appropriately.
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={() => {
              setIsEditing(false);
              generateBusinessDescription();
            }}
            disabled={generating}
            className="bg-blue-600 text-white border-none rounded px-3 py-2 text-xs font-medium cursor-pointer disabled:cursor-not-allowed disabled:opacity-60 flex items-center gap-1 w-auto self-start"
          >
            <i className="fa fa-refresh text-[10px]"></i>
            {generating ? 'Generating...' : 'Regenerate'}
          </button>
          <p className="text-[11px] text-gray-600 leading-snug">
            Uses AI to generate new knowledge based on your <a href="#" onClick={(e) => { e.preventDefault(); window.dispatchEvent(new CustomEvent('switchSettingsTab', { detail: 'business-info' })); }} className="text-blue-600 hover:underline">business information</a>.
          </p>
        </div>
      </form>
    </div>
  );
}

export default BusinessDescription;